<template>
  <table v-if="items?.length && fields?.length" class="table-custom-mini">
    <thead>
      <TableCellHeading v-for="field in fields" :key="field.key">
        {{ field.name }}
      </TableCellHeading>
      <TableCellHeading>
        <slot name="preview-heading" />
      </TableCellHeading>
    </thead>
    <tbody>
      <TableRow
        v-for="item in items"
        :key="item.id"
        :class="{
          'has-sorting': hasSorting,
          'has-sorting__wide': hasSorting && wideSecondColumn,
          selected: item.id === selectedId
        }"
        @onClicked="emits('onClickedRow', item)"
      >
        <TableCellData
          @mouseleave="showTooltip = false"
          @mouseenter="(e) => trackPointer(e, item.name)"
        >
          <p>{{ item.name }}</p>
          <TooltipStandard
            v-show="showTooltip"
            arrowPosition="top-center"
            class="tooltip"
            :pointerEvent="trackedMouseEvent"
            :text="hoveredTitle"
          />
        </TableCellData>
        <TableCellData
          v-if="hasSorting"
          @mouseleave="showTooltip = false"
          @mouseenter="(e) => trackPointer(e, item.sorting ?? '')"
          ><p>{{ item.sorting }}</p>
          <TooltipStandard
            v-show="showTooltip"
            arrowPosition="top-center"
            class="tooltip"
            :pointerEvent="trackedMouseEvent"
            :text="hoveredTitle"
        /></TableCellData>
        <TableCellData v-if="hasMenu && item.active">
          <!-- Need to bind item id to the button for mini menu positioning -->
          <ButtonText
            :id="item.id"
            class="button-no-padding list-group__button"
            :iconLeft="iconMoreVertical"
            size="extra-small"
            :whiteContent="false"
            @onClicked="openMenu(item)"
          ></ButtonText>
        </TableCellData>
        <span v-else-if="hasMenu && !item.active" style="width: 26px"></span>
        <TableCellData>
          <TableMenuMini
            v-if="showMenu && item.id === selectedId"
            :hasArchiveButton="hasArchiveButton"
            :hasDeleteButton="hasDeleteButton"
            :hasDownloadButton="hasDownloadButton"
            :hasEditButton="hasEditButton"
            :position="menuButtonPosition"
            :numberOfButtons="numberOfMenuButtons"
            @archive="emits('archiveItem')"
            @close="closeMenu"
            @edit="emits('editItem', item)"
            @delete="emits('deleteItem')"
            @download="emits('downloadItem')"
          />
        </TableCellData>
        <TableCellData>
          <slot name="preview" :item="item" />
        </TableCellData>
      </TableRow>
    </tbody>
  </table>
  <table v-else class="table-custom-mini">
    <thead>
      <TableCellHeading>
        {{ 0 + ' ' + itemType.toLowerCase() }}
      </TableCellHeading>
    </thead>
    <tbody>
      <TableRow>
        <TableCellData>
          {{
            t('general__text__no') +
            ' ' +
            itemType.toLowerCase() +
            ' ' +
            t('general__text__available').toLowerCase()
          }}
        </TableCellData>
      </TableRow>
    </tbody>
  </table>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import TableMenuMini from '@/design-system/molecules/TableMenuMini.vue'
import TableCellData from '@/design-system/atoms/TableCellData.vue'
import TableCellHeading from '@/design-system/atoms/TableCellHeading.vue'
import TableRow from '@/design-system/atoms/TableRow.vue'
import TooltipStandard from '@/design-system/atoms/TooltipStandard.vue'
import { computed, onUnmounted, ref, watch } from 'vue'
import iconMoreVertical from '@/design-system/assets/icons/Icon-More-vertical.svg'
export type TableItem = {
  id: string
  name: string
  entityId?: string
  fileType?: string
  sorting?: string
  active?: boolean
}

type Props = {
  fields: Array<any>
  hasArchiveButton?: boolean
  hasCreateButton?: boolean
  hasDeleteButton?: boolean
  hasDownloadButton?: boolean
  hasEditButton?: boolean
  hasMenu?: boolean
  hasSorting?: boolean
  items: TableItem[]
  itemType: string
  selectedId: string
  wideSecondColumn?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  hasMenu: true
})
type Emits = {
  (e: 'archiveItem'): void
  (e: 'deleteItem'): void
  (e: 'downloadItem'): void
  (e: 'editItem', item: TableItem): void
  (e: 'loadMore'): void
  (e: 'menuClosed'): void
  (e: 'menuOpened', id: string): void
  (e: 'onClickedRow', item: TableItem): void
}
const emits = defineEmits<Emits>()

const { t } = useI18n()
const store = useStore()

const numberOfMenuButtons = computed(() => {
  let count = 0
  if (props.hasArchiveButton) count++
  if (props.hasDeleteButton) count++
  if (props.hasDownloadButton) count++
  if (props.hasEditButton) count++
  return count
})
const hoveredTitle = ref<string>('')
const infiniteList = ref()
const lastMousePosition = ref([0, 0])
const menuButton = ref<HTMLElement | null>(null)
const menuButtonPosition = ref<{
  x: number
  y: number
} | null>(null)
const showMenu = ref(false)
const showTooltip = ref(false)
const trackedMouseEvent = ref<object>()

function closeMenu() {
  emits('menuClosed')
  menuButton.value = null
  showMenu.value = false
}
function openMenu(item: TableItem) {
  emits('menuOpened', item.id)
  menuButton.value = document.getElementById(item.id)
  showMenu.value = true
}
function setMenuButtonPosition() {
  if (!menuButton.value) return
  const rect = menuButton.value.getBoundingClientRect()
  const bottomMargin = props.hasCreateButton ? 268 : 220 // Lower value is when the bottom container is hidden
  const bottomMarginMobile = numberOfMenuButtons.value > 2 ? 212 : 180
  const leftDashboardMargin = 96
  const topDashboardMargin = 80
  const bottomMax = store.state.mobileScreen
    ? window.innerHeight - bottomMarginMobile
    : window.innerHeight - bottomMargin
  const topMax = store.state.mobileScreen ? topDashboardMargin + 100 : topDashboardMargin + 116
  const yPosition =
    rect.bottom - topDashboardMargin > bottomMax
      ? bottomMax
      : rect.bottom - topDashboardMargin < topMax
      ? topMax
      : rect.bottom - topDashboardMargin
  menuButtonPosition.value = {
    x: rect.left - leftDashboardMargin,
    y: yPosition
  }
}

function trackPointer(event, title: string) {
  hoveredTitle.value = title
  const rect = event.currentTarget.getBoundingClientRect()
  if (event) {
    if (
      lastMousePosition.value[0] === event.clientX &&
      lastMousePosition.value[1] === event.clientY
    )
      return
    lastMousePosition.value = [event.clientX, event.clientY]
    trackedMouseEvent.value = {
      x: rect.left + rect.width / 2,
      y: rect.bottom + rect.height / 2
    }
    const targetEl = event.target as HTMLElement
    if (targetEl.firstElementChild) {
      showTooltip.value =
        targetEl.firstElementChild.clientWidth < targetEl.firstElementChild.scrollWidth
    }
  }
}

watch(infiniteList, () => {
  if (infiniteList.value) {
    infiniteList.value.addEventListener('scroll', () => {
      if (menuButton.value) setMenuButtonPosition()
      if (
        infiniteList.value.scrollTop + infiniteList.value.clientHeight >=
        infiniteList.value.scrollHeight
      ) {
        emits('loadMore')
      }
    })
  }
})
watch(
  menuButton,
  () => {
    if (menuButton.value) {
      setMenuButtonPosition()
      window.addEventListener('resize', setMenuButtonPosition)
    }
    if (!menuButton.value) {
      window.removeEventListener('resize', setMenuButtonPosition)
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  infiniteList.value?.removeEventListener('scroll', emits('loadMore'))
  infiniteList.value?.removeEventListener('scroll', setMenuButtonPosition)
  window.removeEventListener('resize', setMenuButtonPosition)
})
defineExpose({
  closeMenu
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/mixins';
@use '@/design-system/design-tokens/font-variables.scss';
.button-no-padding {
  padding: 0;
}
.tooltip {
  filter: drop-shadow(0px 0px 0.6px rgb(0, 0, 0));
  margin-top: -2px;
  :deep(.tooltip__arrow) {
    filter: unset;
  }
}
.table-custom-mini {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  thead {
    align-items: center;
    background-color: rgba(variables.$color-primary-light, 0.4);
    display: flex;
    height: 32px;
    justify-content: space-between;
    padding: 4px;
    width: 100%;
    th {
      &:nth-child(2) {
        float: right;
        margin-left: auto;
        padding-right: 4px;
      }
      &:last-child {
        justify-content: flex-end;
      }
    }
  }
  tbody {
    display: flex;
    flex-flow: column;
    max-height: max-content;
    min-height: calc(32px * 5); // Five rows
    overflow-x: hidden;
    overflow-y: auto;
    width: 100%;
    @include mixins.scrollbar-vertical;
    tr {
      height: 32px;
      padding: 4px;
      width: 100%;
      p {
        color: variables.$color-greyscale-content;
        margin: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        @include font-mixins.desktop-regular-paragraph;
      }
      td {
        gap: 0;
        @include font-mixins.desktop-regular-paragraph;
        &:first-child {
          width: 224px;
        }
        &:nth-child(2) {
          float: right;
          margin-left: auto;
        }
        &:last-child {
          justify-content: flex-end;
        }
      }
      &.has-sorting {
        td {
          &:first-child {
            max-width: 60%;
          }
          &:nth-child(2) {
            max-width: 80px;
          }
        }
        &__wide {
          td {
            &:first-child {
              max-width: 45%;
            }
            &:nth-child(2) {
              max-width: 100px;
            }
          }
        }
      }
      &.selected {
        background-color: variables.$color-primary;
        td {
          .list-group__button {
            :deep(img) {
              filter: brightness(100);
            }
          }
          p {
            color: white;
          }
        }
      }
      &:hover:not(.selected) {
        background-color: rgba(205, 222, 254, 0.2);
        td {
          .list-group__button {
            background-color: rgba(205, 222, 254, 0.2);
          }
        }
      }
      &:last-child {
        border-radius: 0;
      }
      &:not(:first-child) {
        border-top: none;
      }
    }
  }
}
</style>
